// src/context/CartContext.jsx
import React, { createContext, useContext, useMemo, useState, useEffect } from "react";

const CartCtx = createContext(null);

const STORAGE_KEY = "cart_items";

/**
 * Buang semua field "satuan" agar tidak ada lagi konsep satuan di keranjang,
 * dan normalkan struktur item.
 */
const sanitizeCartItems = (arr) => {
  if (!Array.isArray(arr)) return [];

  return arr
    .map((x) => {
      const id = x?.id ?? x?.product_id ?? null;
      if (!id) return null;

      const qtyNum = Number(x?.qty ?? 0);
      const qty = Number.isFinite(qtyNum) ? Math.max(0, Math.floor(qtyNum)) : 0;

      // ✅ HANYA simpan field yang dibutuhkan
      return {
        id: Number(id) || id, // kalau id angka, simpan angka; kalau string tetap string
        name: x?.name ?? x?.title ?? "Produk",
        qty,
        photo: x?.photo ?? x?.image ?? null,
        stock: x?.stock ?? null,
      };
    })
    .filter((x) => x && x.qty > 0);
};

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      const raw = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
      return sanitizeCartItems(raw);
    } catch {
      return [];
    }
  });

  // ✅ Simpan ke localStorage dalam bentuk sudah disanitasi (tanpa satuan)
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(sanitizeCartItems(items)));
  }, [items]);

  // ✅ ADD tanpa satuan
  const add = (rawProduct, qty = 1) => {
    if (!rawProduct) return;

    const id = rawProduct.id ?? rawProduct.product_id ?? null;
    if (!id) {
      console.warn("Cart.add() diabaikan karena product.id tidak ada:", rawProduct);
      return;
    }

    const addQty = Number(qty);
    const inc = Number.isFinite(addQty) ? Math.max(1, Math.floor(addQty)) : 1;

    const product = {
      id: Number(id) || id,
      name: rawProduct.name ?? rawProduct.title ?? "Produk",
      photo: rawProduct.photo ?? rawProduct.image ?? null,
      stock: rawProduct.stock ?? null,
    };

    setItems((prev) => {
      const safePrev = sanitizeCartItems(prev);

      const exist = safePrev.find((it) => String(it.id) === String(product.id));
      if (exist) {
        return safePrev.map((it) =>
          String(it.id) === String(product.id)
            ? { ...it, qty: (Number(it.qty) || 0) + inc }
            : it
        );
      }

      return [
        ...safePrev,
        {
          id: product.id,
          name: product.name,
          qty: inc,
          photo: product.photo,
          stock: product.stock,
        },
      ];
    });
  };

  const updateQty = (id, qty) => {
    const q = Number(qty);
    const nextQty = Number.isFinite(q) ? Math.max(0, Math.floor(q)) : 0;

    setItems((prev) =>
      sanitizeCartItems(
        prev.map((it) =>
          String(it.id) === String(id) ? { ...it, qty: nextQty } : it
        )
      )
    );
  };

  const remove = (id) => {
    setItems((prev) => sanitizeCartItems(prev.filter((it) => String(it.id) !== String(id))));
  };

  const clear = () => setItems([]);

  const value = useMemo(
    () => ({
      items: sanitizeCartItems(items),
      add,
      updateQty,
      remove,
      clear,
      count: (items || []).reduce((s, it) => s + (Number(it.qty) || 0), 0),
    }),
    [items]
  );

  return <CartCtx.Provider value={value}>{children}</CartCtx.Provider>;
}

export function useCart() {
  const ctx = useContext(CartCtx);
  if (!ctx) throw new Error("useCart must be used inside <CartProvider>");
  return ctx;
}
